import React from 'react';
import { LabelElement } from '../../types/arGuides';
import { clampToViewport, clampTailX, currentViewport } from '../../services/arGuide/clampToViewport';

interface FloatingLabelProps {
  label: LabelElement;
}

const LABEL_W = 200;
const BUBBLE_H = 60;
const MARGEN = 12;

export const FloatingLabel: React.FC<FloatingLabelProps> = ({ label }) => {
  const { text, position, style = 'bubble' } = label;

  if (!position) return null;

  const pos = typeof position === 'object' && 'near' in position ? null : position;

  if (!pos) return null;

  // Posicionamiento del globo respecto al target (coords documento-absolutas):
  // preferimos ARRIBA apuntando hacia abajo; si arriba del target no hay lugar
  // (ítems del header, arriba de la pantalla), el globo va DEBAJO apuntando
  // hacia arriba — nunca encima de lo que está señalando. Finalmente se
  // clampea al viewport (el bug original del texto cortado en el sidebar).
  const altoTarget = Math.max(pos.height ?? 0, 8);
  const espacioArriba = pos.y - currentViewport().scrollY;
  const arriba = espacioArriba >= BUBBLE_H + MARGEN + 24; // globo + cola + aire

  const alto = style === 'bubble' ? BUBBLE_H : 30;
  const yDeseada = arriba
    ? pos.y - alto - 20 // arriba del target, cola hacia abajo
    : pos.y + altoTarget + 8; // debajo del target, cola hacia arriba
  const box = clampToViewport(pos.x - LABEL_W / 2, yDeseada, LABEL_W, alto, currentViewport());
  const tailX = clampTailX(pos.x + (pos.width ?? 0) / 2, box.x, LABEL_W);

  if (style === 'bubble') {
    // La flechita nace en el borde del globo que mira al target y apunta a él.
    const tailBase = arriba ? box.y + BUBBLE_H - 2 : box.y + 2;
    const tailTip = arriba
      ? Math.max(pos.y - 5, tailBase + 14)
      : Math.min(pos.y + altoTarget + 3, tailBase - 14);
    return (
      <g style={{ pointerEvents: 'none' }}>
        <foreignObject
          x={box.x}
          y={box.y}
          width={LABEL_W}
          height={BUBBLE_H}
          style={{ pointerEvents: 'none' }}
        >
          <div
            className="bg-forest text-paper px-4 py-2 rounded-lg shadow-lift text-center"
            style={{ pointerEvents: 'none' }}
          >
            {text}
          </div>
        </foreignObject>
        <polygon
          points={
            arriba
              ? `${tailX},${tailBase} ${tailX - 10},${tailBase} ${tailX},${tailTip}`
              : `${tailX},${tailBase} ${tailX - 10},${tailBase} ${tailX},${tailTip}`
          }
          fill="var(--cm-forest)"
        />
      </g>
    );
  }

  return (
    <foreignObject
      x={box.x}
      y={box.y}
      width={LABEL_W}
      height="30"
      style={{ pointerEvents: 'none' }}
    >
      <div className="bg-ink text-paper px-3 py-1 rounded text-sm" style={{ pointerEvents: 'none' }}>
        {text}
      </div>
    </foreignObject>
  );
};
